import React, { useEffect, useState } from 'react';
import { NotebookPen, Loader2, Save } from 'lucide-react';
import { classroomApi } from '@/lib/classroomApi';
import { toast } from 'sonner';

const Field = ({ label, value, onChange, rows = 3, placeholder, readOnly, testId }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold block mb-1.5">{label}</label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      readOnly={readOnly}
      data-testid={testId}
      className={`w-full border ${readOnly ? 'border-gray-100 bg-gray-50' : 'border-gray-200 focus:border-red-600'} rounded-xl p-3 text-sm outline-none resize-none`}
    />
  </div>
);

const LessonNotesPanel = ({ roomId, token, role }) => {
  const [notes, setNotes] = useState({ summary: '', homework: '', next_recommendation: '', progress_notes: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isTutor = role === 'tutor';

  useEffect(() => {
    (async () => {
      try {
        const r = await classroomApi.notesGet(roomId, token);
        setNotes({
          summary: r.summary || '',
          homework: r.homework || '',
          next_recommendation: r.next_recommendation || '',
          progress_notes: r.progress_notes || '',
        });
      } catch (_e) {}
      finally { setLoading(false); }
    })();
  }, [roomId, token]);

  const save = async () => {
    setSaving(true);
    try {
      await classroomApi.notesPut(roomId, token, notes);
      toast.success('Lesson notes saved');
    } catch (e) { toast.error(e.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="bg-white border border-gray-200 rounded-2xl p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="classroom-notes">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <NotebookPen className="h-4 w-4 text-red-700" />
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Lesson notes</div>
        </div>
        {!isTutor && <span className="text-[10px] uppercase tracking-wider text-gray-400">Read-only</span>}
      </div>
      <div className="p-4 space-y-4">
        <Field label="Summary" value={notes.summary} onChange={(v) => setNotes((s) => ({ ...s, summary: v }))}
               rows={3} placeholder="What was covered this lesson?" readOnly={!isTutor} testId="notes-summary" />
        <Field label="Homework" value={notes.homework} onChange={(v) => setNotes((s) => ({ ...s, homework: v }))}
               rows={3} placeholder="Tasks for the student before next lesson" readOnly={!isTutor} testId="notes-homework" />
        <Field label="Next lesson recommendation" value={notes.next_recommendation} onChange={(v) => setNotes((s) => ({ ...s, next_recommendation: v }))}
               rows={2} placeholder="What should next class focus on?" readOnly={!isTutor} testId="notes-next" />
        <Field label="Progress notes (private to tutor)" value={notes.progress_notes} onChange={(v) => setNotes((s) => ({ ...s, progress_notes: v }))}
               rows={3} placeholder="Strengths, weaknesses, things to revisit" readOnly={!isTutor} testId="notes-progress" />
        {isTutor && (
          <button onClick={save} disabled={saving}
            className="w-full h-11 rounded-full bg-black hover:bg-gray-900 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="notes-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save notes
          </button>
        )}
      </div>
    </div>
  );
};

export default LessonNotesPanel;
